// @ts-strict-ignore
import { useMemo } from 'react';

import capitalize from 'lodash/fp/capitalize';
import { observer } from 'mobx-react';

import { ActionMeta } from 'react-select';

import { useStores } from 'mobx/hooks/useStores';
import { sortPatientTags } from 'mobx/stores/ConstantsStore';

import { TicketSubTypeOption } from 'utils/TicketType.utils';

import { Testable } from 'utils/TypeUtils';

import { FEATURES } from 'constants/features';

import { ClinicianUserStatus } from 'models/Doctor';
import { languageOptions } from 'models/Patient';
import PatientLocation from 'models/PatientLocation';

import PatientProvider from 'models/PatientProvider';

import { TICKET_STATUS_ALL_OPTIONS, TicketStatus } from 'models/Ticket';

import { useClinicianCredentialsOptions } from 'hooks/useClinicianCredentials';
import { useCliniciansAsyncSelect } from 'hooks/useCliniciansAsyncSelect';

import {
  defaultResolvedDateFilterOption,
  RESOLVED_DATE_FILTER_ALL_TIME_OPTION_ID,
  RESOLVED_DATE_FILTER_LAST_WEEK_OPTION_ID,
  RESOLVED_DATE_FILTER_THIS_WEEK_OPTION_ID,
  RESOLVED_DATE_FILTER_YESTERDAY_OPTION_ID,
  ResolvedDateFilterOptionId,
  SPECIAL_FILTERS
} from 'views/Filters/filters.constants';

import {
  useEpisodesMetadataOptions,
  useTaskRoleOptions
} from 'views/Pages/EpisodesManagement/CreateEditPatientEpisodeFormFields';
import {
  useEpisodesManagementOptions,
  useEpisodesNumberOptions
} from 'views/Pages/PracticeManagement/Episodes/EpisodeManagementFilters';
import { ISelectOption } from 'views/Widgets/StyledSelect';
import StyledSummaryMultiSelect from 'views/Widgets/StyledSummaryMultiSelect';

import {
  ItemCategoryId,
  ticketTypesSelectCustomFilter,
  useTicketTypesOptions
} from 'components/Ticket/TicketForms/TicketsInfoFormFragments/OperatorTicketsInfoFormCommon';
import { Select, TicketTypeMultiAutocomplete } from 'components/UIkit/atoms/Dropdown';
import { AsyncBaseSelect } from 'components/UIkit/atoms/Dropdown/BaseSelect';
import { CheckboxesMultiAutocomplete } from 'components/UIkit/atoms/Dropdown/Select/Autocomplete/CheckboxesMultiAutocomplete';
import { SelectVariant } from 'components/UIkit/atoms/Dropdown/Select.shared';

export interface AssigneesMultipleSelectFilterFieldProps<T>
  extends MultipleSelectFilterFieldProps<T> {
  label?: string;
  withUnassignedOption?: boolean;
}

export interface MultipleSelectFilterFieldProps<T> extends Testable {
  value: ISelectOption<T>[];
  onChange: (values: ISelectOption<T>[], actionMeta: ActionMeta<any>) => void;
  variant?: SelectVariant;
  label?: string;
}

export const TagsFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<number>) => {
    const { constantsStore } = useStores();

    const tagOptions = useMemo(() => {
      return sortPatientTags(constantsStore.activeTags).map((tag) => ({
        label: tag.name,
        value: tag.id
      }));
    }, [constantsStore]);

    return (
      <StyledSummaryMultiSelect
        label="Patient Tags"
        options={tagOptions}
        sortAlphabetically={false}
        value={value}
        onChange={onChange}
        testHook="patient-tags-filter"
        variant={variant}
      />
    );
  }
);

export const LocationsFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<number>) => {
    const { locationsStore } = useStores();

    const locationOptions = useMemo(() => {
      return locationsStore.locationsForSelect.map(
        (locationOption: ISelectOption<PatientLocation>) => ({
          label: locationOption.label,
          value: locationOption.value.id
        })
      );
    }, [locationsStore.locationsForSelect]);

    return (
      <StyledSummaryMultiSelect
        label="Locations"
        options={locationOptions}
        value={value}
        onChange={onChange}
        testHook="locations-filter"
        variant={variant}
      />
    );
  }
);

export const RegionsFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<number>) => {
    const { locationsStore } = useStores();
    const options = locationsStore.regionsForSelect;

    return (
      <CheckboxesMultiAutocomplete
        label="Locations"
        value={value}
        onChange={onChange}
        options={options}
        testHook="locations-filter"
        variant={variant}
      />
    );
  }
);

export const ProvidersFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<PatientProvider>) => {
    const { providersStore } = useStores();
    const providerOptions = providersStore.providersForSelect;
    return (
      <StyledSummaryMultiSelect
        label="Providers"
        options={providerOptions}
        value={value}
        onChange={onChange}
        getOptionValue={(option) => option.value.id}
        testHook="providers-filter"
        variant={variant}
      />
    );
  }
);

export const LanguageFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<string>) => {
    return (
      <StyledSummaryMultiSelect
        label="Preferred Language"
        options={languageOptions}
        value={value}
        onChange={onChange}
        testHook="languages-filter"
        variant={variant}
      />
    );
  }
);

export enum RemoteMonitoringStatus {
  active = 'Active',
  optedOut = 'Opted Out',
  notInvited = 'Not Invited',
  invited = 'Invited',
  invitedNoResponse = 'Invited - No Response',
  awaitingActivation = 'Awaiting Activation'
}

export const CliniciansFilterField = observer(
  ({
    value,
    onChange,
    label = 'Assignees',
    withUnassignedOption = true,
    variant
  }: AssigneesMultipleSelectFilterFieldProps<number | SPECIAL_FILTERS>) => {
    const { loadOptions, defaultOptions } = useCliniciansAsyncSelect({
      withUnassignedOption
    });

    return (
      <AsyncBaseSelect
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        isMulti
        label={label}
        defaultOptions={defaultOptions}
        testHook="assignees-filter"
        displaySelectedSummary
        variant={variant}
      />
    );
  }
);

interface TicketTypeFilterFieldProps {
  value: TicketSubTypeOption[];
  onChange: (values: TicketSubTypeOption[], actionMeta: ActionMeta<any>) => void;
  itemCategoryIdsOrder?: ItemCategoryId[];
  variant?: SelectVariant;
}

export const TicketTypeFilterField = observer(
  ({ value, onChange, itemCategoryIdsOrder, variant = 'primary' }: TicketTypeFilterFieldProps) => {
    const { settingsStore } = useStores();
    const options = useTicketTypesOptions({
      // do not show deleted types which no are not used by any ticket
      filterFn: (node) => node.isInUse,
      addCategoryAllOption: true,
      addItemCategories: true,
      itemCategoryIdsOrder,
      addTicketTypesAndSubTypes: settingsStore.hasFeature(FEATURES.TICKET_TYPES_AND_SUB_TYPES)
    });

    return (
      <TicketTypeMultiAutocomplete
        label="Item Types"
        options={options}
        sortAlphabetically={false}
        value={value}
        onChange={onChange}
        filterOption={ticketTypesSelectCustomFilter}
        variant={variant}
      />
    );
  }
);

export const ClinicianUserStatusFilterField = ({
  onChange,
  value
}: MultipleSelectFilterFieldProps<ClinicianUserStatus>) => {
  const options = useMemo(() => {
    return Object.values(ClinicianUserStatus).map((status: string) => ({
      label: capitalize(status),
      value: status
    }));
  }, []);

  return (
    <StyledSummaryMultiSelect
      label="User Status"
      options={options}
      value={value}
      onChange={onChange}
    />
  );
};

export const ClinicianCredentialsFilterField = ({
  onChange,
  value
}: MultipleSelectFilterFieldProps<number>) => {
  const clinicianCredentialOptions = useClinicianCredentialsOptions();

  return (
    <StyledSummaryMultiSelect
      label="Credentials"
      options={clinicianCredentialOptions}
      value={value}
      onChange={onChange}
      sortAlphabetically={false} // None should appear first
    />
  );
};

export const RoleFilterField = ({ onChange, value }: MultipleSelectFilterFieldProps<number>) => {
  const { constantsStore } = useStores();
  return (
    <StyledSummaryMultiSelect
      label="Role"
      options={constantsStore.getRolesForSelect()}
      value={value}
      sortAlphabetically={false}
      onChange={onChange}
    />
  );
};

export const EpisodesFilterField = observer(
  ({ value, onChange, variant }: MultipleSelectFilterFieldProps<number>) => {
    const options = useEpisodesMetadataOptions();
    return (
      <StyledSummaryMultiSelect
        label="Episodes"
        options={options}
        value={value}
        onChange={onChange}
        testHook="episodes-filter"
        variant={variant}
      />
    );
  }
);

interface EpisodeNumberFilterFieldProps extends MultipleSelectFilterFieldProps<number> {
  patientId: number;
}
export const EpisodeNumberFilterField = observer(
  ({ value, onChange, patientId }: EpisodeNumberFilterFieldProps) => {
    const options: any = useEpisodesNumberOptions(patientId);
    return (
      <StyledSummaryMultiSelect
        label="Episode Number"
        options={options}
        isDisabled={options.length === 0}
        value={value}
        onChange={onChange}
      />
    );
  }
);

export const ManageEpisodesFilterField = observer(
  ({ value, onChange }: MultipleSelectFilterFieldProps<number>) => {
    const options = useEpisodesManagementOptions();
    return (
      <StyledSummaryMultiSelect
        label="Episodes"
        options={options}
        value={value}
        onChange={onChange}
      />
    );
  }
);

interface TaskStatusFilterFieldProps extends MultipleSelectFilterFieldProps<TicketStatus> {
  options?: ISelectOption<TicketStatus>[];
}
export const TaskStatusFilterField = ({
  value,
  onChange,
  options = TICKET_STATUS_ALL_OPTIONS,
  variant,
  label = 'Task Status'
}: TaskStatusFilterFieldProps) => {
  return (
    <StyledSummaryMultiSelect
      label={label}
      options={options}
      value={value}
      onChange={onChange}
      testHook="task-status-filter"
      variant={variant}
    />
  );
};

export const EpisodeOwnersFilterField = ({
  value,
  onChange,
  variant,
  label = 'Task Owner'
}: MultipleSelectFilterFieldProps<number>) => {
  const { loadOptions, defaultOptions } = useCliniciansAsyncSelect();

  return (
    <AsyncBaseSelect
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      isMulti
      label={label}
      defaultOptions={defaultOptions}
      testHook="task-owner-filter"
      variant={variant}
    />
  );
};

export const TaskRolesFilterField = ({
  value,
  onChange,
  variant,
  label = 'Task Role'
}: MultipleSelectFilterFieldProps<number>) => {
  const options = useTaskRoleOptions();
  return (
    <StyledSummaryMultiSelect
      label={label}
      options={options}
      value={value}
      sortAlphabetically={false}
      onChange={onChange}
      testHook="task-role-filter"
      variant={variant}
    />
  );
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
// options for last name first letter filter - each option is like: { value: 'A', label: 'A' }
const ALPHABETICAL_OPTIONS = ALPHABET.split('').map((letter) => ({
  value: letter,
  label: letter
}));

export const LastNameFirstLetterFilterField = ({
  value,
  onChange,
  variant
}: MultipleSelectFilterFieldProps<string>) => {
  return (
    <StyledSummaryMultiSelect
      label="Tasks by Last Name"
      options={ALPHABETICAL_OPTIONS}
      value={value}
      sortAlphabetically={false}
      onChange={onChange}
      testHook="last-name-first-letter-filter"
      variant={variant}
    />
  );
};

export const ResolvedDateFilterField = ({
  value,
  onChange,
  variant
}: {
  value: ISelectOption<ResolvedDateFilterOptionId>;
  onChange: (
    values: ISelectOption<ResolvedDateFilterOptionId>,
    actionMeta: ActionMeta<any>
  ) => void;
  variant: SelectVariant;
}) => (
  <Select
    variant={variant}
    value={value}
    options={[
      defaultResolvedDateFilterOption,
      { value: RESOLVED_DATE_FILTER_YESTERDAY_OPTION_ID, label: 'Yesterday' },
      { value: RESOLVED_DATE_FILTER_THIS_WEEK_OPTION_ID, label: 'This Week' },
      { value: RESOLVED_DATE_FILTER_LAST_WEEK_OPTION_ID, label: 'Last Week' },
      { value: RESOLVED_DATE_FILTER_ALL_TIME_OPTION_ID, label: 'All Time' }
    ]}
    onChange={onChange}
    label="Resolved Date"
    sortAlphabetically={false}
    isClearable={false}
  />
);
